'use client';
import { useState, type SyntheticEvent } from 'react';
import axios from 'axios';
import { useGoogleLogin as useGoogleOAuth } from '@react-oauth/google';
import { AuthButton } from '../AuthButton/AuthButton';
import { AuthFormWrapper } from '../AuthFormWrapper/AuthFormWrapper';
import { AuthInput } from '../AuthInput/AuthInput';
import { Checkbox, useToast, GoogleIcon } from '@practium/ui';
import { LegalModal, useLegalPolicies } from '@practium/features/policy';
import styles from './RegisterForm.module.css';
import type { RegisterCredentials } from '../../types/auth.types';
import {
	useRegister,
	useGoogleRegister,
	useActivePolicies,
	useLogin,
	useTrackCheckoutStart,
} from '@practium/features/api';
import type { ActivePolicyVersion } from '@practium/features/api';
import {
	validateEmail,
	validateName,
	validatePassword,
	validatePasswordMatch,
	validateTermsAcceptance,
} from '../../validations/auth.validation';

function extractApiErrorMessage(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error) && error.response?.data?.message) {
		return error.response.data.message;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
}

interface GoogleRegisterButtonProps {
	acceptedTerms: boolean;
	activePolicies: ActivePolicyVersion[];
	onSuccess: () => void;
	disabled: boolean;
}

const GoogleRegisterButton = ({
	acceptedTerms,
	activePolicies,
	onSuccess,
	disabled,
}: GoogleRegisterButtonProps) => {
	const toast = useToast();
	const { mutate: googleRegister, isPending } = useGoogleRegister();

	const triggerGooglePopup = useGoogleOAuth({
		scope: 'openid email profile',
		onSuccess: (codeResponse) => {
			const consents = activePolicies.map((policy) => ({
				policyVersionId: policy._id,
				accepted: true,
			}));

			googleRegister(
				{ code: codeResponse.code, consents },
				{
					onSuccess,
					onError: (err) => {
						toast.error(extractApiErrorMessage(err, 'Erro ao cadastrar com o Google'));
					},
				}
			);
		},
		onError: () => {
			toast.error('Não foi possível abrir a janela de cadastro do Google.');
		},
		flow: 'auth-code',
	});

	const handleClick = () => {
		const termsValidation = validateTermsAcceptance(acceptedTerms);
		if (!termsValidation.isValid) {
			toast.error(termsValidation.error!);
			return;
		}
		triggerGooglePopup();
	};

	return (
		<AuthButton
			variant="social"
			fullWidth
			icon={<GoogleIcon />}
			onClick={handleClick}
			disabled={disabled || isPending}
			isLoading={isPending}
		>
			Continuar com Google
		</AuthButton>
	);
};

interface RegisterFormProps {
	onSuccess?: () => void;
	onGoogleSuccess?: () => void;
	onAutoLoginFailed?: () => void;
}

export const RegisterForm = ({ onSuccess, onGoogleSuccess, onAutoLoginFailed }: RegisterFormProps = {}) => {
	const toast = useToast();
	const { termsOfService, privacyPolicy } = useLegalPolicies();
	const { mutate: register, isPending } = useRegister();
	const { mutate: login, isPending: isLoggingIn } = useLogin();
	const { data: activePolicies = [] } = useActivePolicies();
	const { mutate: trackCheckoutStart } = useTrackCheckoutStart();
	const hasGoogleClient = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

	const [name, setName] = useState('');
	const [surName, setSurName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [acceptedTerms, setAcceptedTerms] = useState(false);

	const [showTermsModal, setShowTermsModal] = useState(false);
	const [showPrivacyModal, setShowPrivacyModal] = useState(false);

	const handleSubmit = (e: SyntheticEvent) => {
		e.preventDefault();

		const emailValidation = validateEmail(email);
		if (!emailValidation.isValid) {
			toast.error(emailValidation.error!);
			return;
		}

		const nameValidation = validateName(name);
		if (!nameValidation.isValid) {
			toast.error(nameValidation.error!);
			return;
		}

		if (surName) {
			const surNameValidation = validateName(surName);
			if (!surNameValidation.isValid) {
				toast.error(surNameValidation.error!);
				return;
			}
		}

		const passwordValidation = validatePassword(password);
		if (!passwordValidation.isValid) {
			toast.error(passwordValidation.errors[0]);
			return;
		}

		const passwordMatchValidation = validatePasswordMatch(password, confirmPassword);
		if (!passwordMatchValidation.isValid) {
			toast.error(passwordMatchValidation.error!);
			return;
		}

		const termsValidation = validateTermsAcceptance(acceptedTerms);
		if (!termsValidation.isValid) {
			toast.error(termsValidation.error!);
			return;
		}

		const credentials: RegisterCredentials = {
			name,
			surName,
			email,
			password,
			confirmPassword,
			acceptedTerms,
		};

		register(
			{ ...credentials, activePolicies },
			{
				onSuccess: () => {
					login(
						{ email, password, rememberMe: true },
						{
							onSuccess: () => {
								trackCheckoutStart({ step: 'cadastro' });
								toast.success('Cadastro realizado com sucesso!');
								onSuccess?.();
							},
							onError: () => {
								toast.success('Cadastro realizado com sucesso! Faça login para continuar.');
								if (onAutoLoginFailed) onAutoLoginFailed();
								else onSuccess?.();
							},
						}
					);
				},
				onError: (err) => {
					const message = err instanceof Error ? err.message : 'Erro ao criar conta';
					toast.error(message);
				},
			}
		);
	};

	const handleGoogleSuccess = () => {
		trackCheckoutStart({ step: 'cadastro' });
		toast.success('Cadastro realizado com sucesso!');
		if (onGoogleSuccess) onGoogleSuccess();
	};

	return (
		<>
			<AuthFormWrapper
				onSubmit={handleSubmit}
				socialButtons={
					<>
						{hasGoogleClient ? (
							<GoogleRegisterButton
								acceptedTerms={acceptedTerms}
								activePolicies={activePolicies}
								onSuccess={handleGoogleSuccess}
								disabled={isPending}
							/>
						) : (
							<AuthButton variant="social" fullWidth icon={<GoogleIcon />} disabled>
								Continuar com Google
							</AuthButton>
						)}
					</>
				}
			>
				<div className={styles.inputWrapper}>
					<div className={styles.nameRow}>
						<AuthInput
							label="Nome"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="João"
							required
						/>
						<AuthInput
							label="Sobrenome (opcional)"
							type="text"
							value={surName}
							onChange={(e) => setSurName(e.target.value)}
							placeholder="Silva"
						/>
					</div>
					<AuthInput
						label="E-mail"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="exemplo@gmail.com"
						required
					/>
					<AuthInput
						label="Senha"
						isPassword
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						placeholder="abc123"
						required
					/>
					<AuthInput
						label="Confirmar senha"
						isPassword
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						placeholder="abc123"
						required
					/>
				</div>

				<Checkbox
					label={
						<>
							Declaro que li e aceito os{' '}
							<button
								type="button"
								className={styles.termsLink}
								onClick={(e) => {
									e.preventDefault();
									setShowTermsModal(true);
								}}
							>
								Termos de Uso
							</button>{' '}
							|{' '}
							<button
								type="button"
								className={styles.termsLink}
								onClick={(e) => {
									e.preventDefault();
									setShowPrivacyModal(true);
								}}
							>
								Política de Privacidade
							</button>
						</>
					}
					checked={acceptedTerms}
					onChange={(e) => setAcceptedTerms(e.target.checked)}
				/>

				<AuthButton type="submit" variant="primary" fullWidth isLoading={isPending || isLoggingIn}>
					Criar Conta
				</AuthButton>
			</AuthFormWrapper>

			<LegalModal
				open={showTermsModal}
				onClose={() => setShowTermsModal(false)}
				policy={termsOfService}
			/>
			<LegalModal
				open={showPrivacyModal}
				onClose={() => setShowPrivacyModal(false)}
				policy={privacyPolicy}
			/>
		</>
	);
};
